import _set from 'lodash/set'
import * as z from 'zod'
import type {
  ZodEffects,
  ZodEnum,
  ZodIssueOptionalMessage,
  ZodNumber,
  ZodObject,
  ZodPipeline,
  ZodRawShape,
  ZodTypeAny,
} from 'zod'

import { t } from '@flows/core/i18n'
import type { TranslationKey } from '@flows/core/i18n/types'

export type SchemaPath = ZodEffects<ZodTypeAny> | ZodTypeAny

// Recursively generates a zod object from the provided object
const recursivelyGenerateZodObjectFromJSObject = (
  obj: { [key: string]: SchemaPath } | SchemaPath
): ZodObject<ZodRawShape> => {
  if (obj instanceof z.Schema) {
    return obj as ZodObject<ZodRawShape>
  }
  let zObj = z.object({})
  Object.keys(obj).forEach((key) => {
    zObj = zObj.merge(z.object({ [key]: recursivelyGenerateZodObjectFromJSObject(obj[key]) }))
  })

  return zObj
}

/**
 * generateZodObjectFromPath converts an object containing paths as string
 * to a valid zod object schema.
 * This is useful when validating a schema in a step that uses functions such as
 * getParamPath or getProductHelperPath to maintain compatibility with the base schema
 */
export const generateZodObjectFromPaths = (
  pathsObject: Record<string, SchemaPath>
): ZodObject<ZodRawShape> => {
  const simpleObject = {}
  Object.keys(pathsObject).forEach((key) => {
    _set(simpleObject, key, pathsObject[key])
  })

  return recursivelyGenerateZodObjectFromJSObject(simpleObject)
}

export const bornOnErrorMap = (
  issue: ZodIssueOptionalMessage,
  messages: { min?: TranslationKey; max?: TranslationKey } = {}
): { message: string } | undefined => {
  const { min, max } = messages
  switch (issue.code) {
    case 'too_big':
      return { message: t(min ?? 'sharedFlows_common:generic.errors.min_age') }
    case 'too_small':
      return { message: t(max ?? 'sharedFlows_common:generic.errors.max_age') }
    default:
      return undefined
  }
}

export const zipCodeErrorMap = (
  issue: ZodIssueOptionalMessage,
  customErrorkey?: string
): { message: string } | undefined => {
  if (
    issue.path.includes('zip_code') &&
    issue.code === z.ZodIssueCode.invalid_string &&
    issue.validation === 'regex'
  ) {
    return { message: t(customErrorkey ?? 'sharedFlows_common:steps.zip_code.error.notValid') }
  }
  return undefined
}

export const getMinMaxSchemaValue = (schema: ZodTypeAny, type: 'min' | 'max'): number => {
  const value = schema._def.checks.find(({ kind }: { kind: 'min' | 'max' }) => kind === type)?.value
  if (value === undefined) {
    throw new Error('Assertion: min/max value is undefined')
  }
  return value
}

// The main reason behind this is: zod does not allow type inspection inside pipeline schema
// ZodPipeline does not have innerType property
// Check https://github.com/colinhacks/zod/issues/1833
export const getEnumValuesFromPipelineSchema = (
  pipeline: z.ZodPipeline<ZodPipeline<ZodTypeAny, ZodEnum<[string, ...string[]]>>, ZodNumber>
) => {
  const {
    in: {
      _def: {
        out: {
          _def: { values: enumValues },
        },
      },
    },
  } = pipeline._def

  return enumValues
}
